// src/components/RiqChat.js
import React, { useState, useRef, useEffect } from 'react'
import { supabase } from '../supabaseClient'

function RiqChat() {
  const [riqQuestion, setRiqQuestion] = useState('')
  const [riqAnswerLog, setRiqAnswerLog] = useState([]) // {question, answer, isThinking}
  const [pendingCandidates, setPendingCandidates] = useState(null)
  const [activePlanId, setActivePlanId] = useState(null)
  const [activePlanName, setActivePlanName] = useState(null)
  const [activePlanYear, setActivePlanYear] = useState(null)

  const chatEndRef = useRef(null)

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [riqAnswerLog])

  // Dynamic placeholder: helpful hint only before first question, then simple.
  const getPlaceholder = () => {
    // Before any questions have been asked
    if (riqAnswerLog.length === 0) {
      return 'Ask questions regarding coverage on a particular plan or how to use the platform'
    }

    // After chat has started AND a plan is selected
    if (activePlanId && activePlanName) {
      return 'Ask coverage question…'
    }

    // After chat has started but no plan selected
    return 'Ask your question for Riq…'
  }

  const handleAskRiq = async () => {
    if (!riqQuestion.trim()) return
    const question = riqQuestion.trim()
    setRiqQuestion('')

    // Grab previous completed Q&A turn BEFORE adding this new one
    let previousQuestion = null
    let previousAnswer = null
    if (riqAnswerLog.length > 0) {
      const lastTurn = riqAnswerLog[riqAnswerLog.length - 1]
      if (lastTurn && lastTurn.answer && lastTurn.answer.trim()) {
        previousQuestion = lastTurn.question
        previousAnswer = lastTurn.answer
      }
    }

    // Add new entry in "thinking" state
    setRiqAnswerLog((prev) => [
      ...prev,
      { question, answer: '', isThinking: true },
    ])

    const lower = question.toLowerCase()

    // Local reset (no server call needed)
    if (lower.includes('reset') || lower.includes('change plan')) {
      setActivePlanId(null)
      setActivePlanName(null)
      setActivePlanYear(null)
      setPendingCandidates(null)
      setRiqAnswerLog((prev) =>
        prev.map((e, i) =>
          i === prev.length - 1
            ? {
                ...e,
                isThinking: false,
                answer:
                  '🔄 Group plan context has been reset. You can now ask about a new insurance plan using group name or group ID.',
              }
            : e
        )
      )
      return
    }

    // If we have a list of candidates and the user types a number, treat as selection
    if (pendingCandidates && /^[1-9][0-9]*$/.test(question)) {
      const idx = parseInt(question, 10) - 1
      const selected = pendingCandidates[idx]
      if (selected) {
        setActivePlanId(selected.id)
        setActivePlanName(selected.group_name)
        setActivePlanYear(selected.benefit_year)
        setPendingCandidates(null)
        setRiqAnswerLog((prev) =>
          prev.map((e, i) =>
            i === prev.length - 1
              ? {
                  ...e,
                  isThinking: false,
                  answer: `✅ You selected: **${selected.group_name} Plan Year ${selected.benefit_year}**. You can now ask  questions about this plan.`,
                }
              : e
          )
        )
      } else {
        setRiqAnswerLog((prev) =>
          prev.map((e, i) =>
            i === prev.length - 1
              ? {
                  ...e,
                  isThinking: false,
                  answer: 'Invalid selection. Please choose a number from the list.',
                }
              : e
          )
        )
      }
      return
    }

    // Normal flow: send to /api/riq
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser()

      const headers = {
        'Content-Type': 'application/json',
        'x-user-id': user?.id || '',
        'x-user-timezone':
          Intl.DateTimeFormat().resolvedOptions().timeZone ||
          'America/Los_Angeles',
      }

      if (activePlanId) headers['x-active-plan-id'] = activePlanId

      const body = { query: question }

      // Only send context if we have a completed prior turn
      if (previousQuestion && previousAnswer) {
        body.context = {
          previousQuestion,
          previousAnswer,
        }
      }

      const res = await fetch('/api/riq', {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      })

      const data = await res.json()

      let answerText = ''
      const mode = data.mode || 'coverage'

      if (!data.success && (mode === 'need_plan' || mode === 'no_match')) {
        answerText =
          data.message ||
          data.error ||
          'Sorry, I could not find a matching plan.'
      } else if (!data.success) {
        answerText = data.error || 'Sorry, I could not process that.'
      } else {
        switch (mode) {
          case 'general':
          case 'faq':
          case 'reset':
            answerText = data.answer
            break

          case 'coverage':
            // remember which plan was actually used
            if (data.usedPlanId) {
              setActivePlanId(data.usedPlanId)
              setActivePlanName(data.usedPlanName || activePlanName || null)
              setActivePlanYear(data.usedPlanYear || null)
            }
            answerText = data.answer
            break

          case 'plan_selected':
            setActivePlanId(data.selectedPlanId)
            setActivePlanName(data.selectedPlanName)
            setActivePlanYear(data.selectedPlanYear || null)
            answerText = data.answer
            break

          case 'candidates': {
            const candidates = data.candidates || []
            setPendingCandidates(candidates)
            const list = candidates
              .map(
                (c, i) =>
                  `${i + 1}. ${c.group_name}${
                    c.group_id ? ` (Group ID: ${c.group_id})` : ''
                  }`
              )
              .join('\n')
            answerText = `${
              data.message ||
              'I found multiple matching plans. Please choose one:'
            }\n\n${list}`
            break
          }

          default:
            answerText =
              data.answer ||
              data.message ||
              'I am not sure how to handle that yet. Please try rephrasing your question.'
        }
      }

      setRiqAnswerLog((prev) =>
        prev.map((e, i) =>
          i === prev.length - 1
            ? { ...e, isThinking: false, answer: answerText }
            : e
        )
      )
    } catch (err) {
      setRiqAnswerLog((prev) =>
        prev.map((e, i) =>
          i === prev.length - 1
            ? {
                ...e,
                isThinking: false,
                answer: `❌ Failed to reach Riq: ${err.message}`,
              }
            : e
        )
      )
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Chat log */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          paddingRight: '1rem',
          paddingBottom: '1rem',
        }}
      >
        {riqAnswerLog.map((entry, idx) => (
          <div
            key={idx}
            style={{
              marginBottom: '1rem',
              background: '#f4f4f4',
              padding: '1rem',
              borderRadius: '5px',
            }}
          >
            <strong>You:</strong>
            <p>{entry.question}</p>
            <strong>Riq:</strong>
            {entry.isThinking ? (
              <div className="riq-thinking">
                <span className="riq-thinking-dot" />
                <span className="riq-thinking-dot" />
                <span className="riq-thinking-dot" />
              </div>
            ) : (
              <pre style={{ whiteSpace: 'pre-wrap' }}>{entry.answer}</pre>
            )}
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      {/* Active plan banner */}
      {activePlanId && (
        <p
          style={{
            fontSize: '0.85rem',
            color: '#007bff',
            margin: '0 0 0.5rem 0',
          }}
        >
          🔍 Answering questions related to: <strong>{activePlanName} {activePlanYear ? `Plan Year ${activePlanYear}` : ''}</strong> — type
          "reset" to choose another plan.
        </p>
      )}

      {/* Input box + Submit */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '1rem',
          borderTop: '1px solid #ddd',
          background: '#fff',
        }}
      >
        <textarea
          rows="2"
          style={{
            flex: 1,
            width: '100%',
            minHeight: '3rem',
            resize: 'none',
            padding: '0.5rem',
            borderRadius: '5px',
            border: '1px solid #ccc',
            fontSize: '1rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'wrap',
          }}
          placeholder={getPlaceholder()}
          value={riqQuestion}
          onChange={(e) => setRiqQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleAskRiq()
            }
          }}
        />
        <button
          onClick={handleAskRiq}
          disabled={!riqQuestion.trim()}
          style={{
            padding: '0.6rem 1rem',
            fontSize: '1rem',
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Submit
        </button>
      </div>
    </div>
  )
}

export default RiqChat
